import {
  mysqlTable,
  int,
  varchar,
  text,
  longtext,
  boolean,
  smallint,
  date,
  timestamp,
  mysqlEnum,
  index,
  unique,
} from "drizzle-orm/mysql-core";
import { companies } from "./tenancy";
import { users } from "./identity";
import { letters } from "./letter";

/**
 * Lifecycle module (employee confirmation/transfer/separation/probation flows + clearance +
 * exit interviews). Ported line-by-line from Modules/Lifecycle/Database/Migrations/* (2026_06_*).
 * company_id NOT NULL; lifecycle_events uses softDeletes. JSON payloads stored as longText
 * (legacy `longText('*_json')`). Legacy custom FK constraint names (lce_*, lci_*, …) are not
 * reproduced (Drizzle auto-names; behaviour identical); explicit index/unique names ARE kept.
 */

export const lifecycleEvents = mysqlTable(
  "lifecycle_events",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    eventType: mysqlEnum("event_type", [
      "confirmation",
      "transfer",
      "separation",
      "probation_extension",
    ]).notNull(),
    status: mysqlEnum("status", [
      "draft",
      "pending_approval",
      "approved",
      "rejected",
      "in_progress",
      "completed",
      "cancelled",
    ])
      .default("draft")
      .notNull(),
    effectiveDate: date("effective_date").notNull(),
    payloadJson: longtext("payload_json"),
    currentApproverId: int("current_approver_id", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    approverChainJson: longtext("approver_chain_json"),
    initiatedBy: int("initiated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    initiatedAt: timestamp("initiated_at"),
    completedAt: timestamp("completed_at"),
    letterInstanceId: int("letter_instance_id", { unsigned: true }).references(() => letters.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
    deletedAt: timestamp("deleted_at"),
  },
  (t) => [
    index("lce_lookup_idx").on(t.companyId, t.userId, t.eventType, t.status),
  ],
);

export const lifecycleClearanceItems = mysqlTable(
  "lifecycle_clearance_items",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    eventId: int("event_id", { unsigned: true })
      .notNull()
      .references(() => lifecycleEvents.id, { onDelete: "cascade", onUpdate: "cascade" }),
    departmentKey: varchar("department_key", { length: 64 }).notNull(),
    assigneeId: int("assignee_id", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    checklistJson: longtext("checklist_json"),
    status: mysqlEnum("status", ["pending", "in_progress", "cleared", "blocked"])
      .default("pending")
      .notNull(),
    blockingReason: text("blocking_reason"),
    clearedAt: timestamp("cleared_at"),
    clearedBy: int("cleared_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("lci_event_status_idx").on(t.eventId, t.status),
    index("lci_assignee_idx").on(t.companyId, t.assigneeId, t.status),
  ],
);

export const lifecycleClearanceDepartmentTemplates = mysqlTable(
  "lifecycle_clearance_department_templates",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    departmentKey: varchar("department_key", { length: 64 }).notNull(),
    displayName: varchar("display_name", { length: 255 }).notNull(),
    defaultChecklistJson: longtext("default_checklist_json"),
    active: boolean("active").default(true).notNull(),
    sortOrder: smallint("sort_order", { unsigned: true }).default(0).notNull(),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [unique("lcdt_unique_dept").on(t.companyId, t.departmentKey)],
);

export const lifecycleExitInterviews = mysqlTable(
  "lifecycle_exit_interviews",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    eventId: int("event_id", { unsigned: true })
      .notNull()
      .references(() => lifecycleEvents.id, { onDelete: "cascade", onUpdate: "cascade" }),
    conductedBy: int("conducted_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    conductedAt: timestamp("conducted_at"),
    responsesJson: longtext("responses_json"),
    summary: text("summary"),
    status: mysqlEnum("status", ["pending", "scheduled", "completed", "skipped"])
      .default("pending")
      .notNull(),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [unique("lei_unique_event").on(t.eventId)],
);
